// pages/admin/TestimonialForm.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MdSave, MdArrowBack, MdStar } from 'react-icons/md';

export default function TestimonialForm() {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    name: '',
    location: '',
    text: '',
    rating: 5,
    is_active: true,
    sort_order: 0,
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await axios.get(
          `${base_url}/api/v1/testimonials/admin/${id}`
        );
        if (res.data.success) {
          const t = res.data.data;
          setForm({
            name: t.name || '',
            location: t.location || '',
            text: t.text || '',
            rating: t.rating || 5,
            is_active: !!t.is_active,
            sort_order: t.sort_order ?? 0,
          });
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, base_url]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({
      ...p,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.text.trim()) {
      setError('Name and testimonial text are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        location: form.location.trim() || null,
        text: form.text.trim(),
        rating: parseInt(form.rating) || 5,
        is_active: !!form.is_active,
        sort_order: parseInt(form.sort_order) || 0,
      };

      if (isEdit) {
        await axios.put(
          `${base_url}/api/v1/testimonials/${id}`,
          payload
        );
      } else {
        await axios.post(`${base_url}/api/v1/testimonials`, payload);
      }

      navigate('/dashboard/mbbs/testimonials');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid mt-5 text-center">
        <div className="spinner-border text-primary" />
        <div className="mt-2">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="container-fluid mt-3">
        <div className="card shadow border-0">
          <div className="card-header bg-white d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => navigate('/dashboard/testimonials')}
            >
              <MdArrowBack />
            </button>
            <h4 className="fw-bold mb-0">
              {isEdit ? 'Edit Testimonial' : 'Add Testimonial'}
            </h4>
          </div>

          <form onSubmit={handleSubmit} className="card-body">
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="e.g. Priya Sharma"
                  required
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Location / Role</label>
                <input
                  type="text"
                  className="form-control"
                  name="location"
                  value={form.location}
                  onChange={onChange}
                  placeholder="e.g. NEET 2024 Aspirant"
                />
              </div>

              <div className="col-12">
                <label className="form-label fw-bold">
                  Testimonial Text <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  name="text"
                  rows="5"
                  value={form.text}
                  onChange={onChange}
                  placeholder="What did the student/parent say about MBBS Dunia?"
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Rating</label>
                <select
                  className="form-select"
                  name="rating"
                  value={form.rating}
                  onChange={onChange}
                >
                  {[5, 4, 3, 2, 1].map((r) => (
                    <option key={r} value={r}>
                      {'★'.repeat(r)} ({r} star{r > 1 ? 's' : ''})
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label">Sort Order</label>
                <input
                  type="number"
                  className="form-control"
                  name="sort_order"
                  value={form.sort_order}
                  onChange={onChange}
                  min="0"
                  placeholder="Higher = appears first"
                />
                <div className="form-text">
                  Higher numbers appear earlier in the slider.
                </div>
              </div>

              <div className="col-md-4 d-flex align-items-center">
                <div className="form-check form-switch mt-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={form.is_active}
                    onChange={onChange}
                  />
                  <label
                    className="form-check-label fw-bold"
                    htmlFor="is_active"
                  >
                    Active (visible on website)
                  </label>
                </div>
              </div>

              {/* Preview */}
              <div className="col-12">
                <label className="form-label">Live Preview</label>
                <div
                  className="border rounded p-3"
                  style={{ maxWidth: 340, background: '#f9fafb' }}
                >
                  <div className="text-center">
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        background: '#01457B',
                        color: 'white',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 20,
                        fontWeight: 700,
                      }}
                    >
                      {(form.name || 'U').trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="text-warning my-2">
                      {'★'.repeat(form.rating)}
                      <span className="text-muted">
                        {'★'.repeat(5 - form.rating)}
                      </span>
                    </div>
                    <p className="fst-italic small mb-2">
                      &ldquo;{form.text || 'Testimonial text will appear here'}&rdquo;
                    </p>
                    <strong className="d-block">
                      {form.name || 'Name'}
                    </strong>
                    <span className="text-muted small">
                      {form.location || 'Location'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 mt-4">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                <MdSave />{' '}
                {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate('/dashboard/mbbs/testimonials')}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}